/**
 * Centralised Segment catalogue for the Seller Collects buyer journey. Event
 * names, property keys and the surfaces an explainer can be opened from live
 * here as named exports, so no Seller Collects component reaches for a string
 * literal and no two call sites can spell a property differently.
 *
 * Why these events exist: the PRD's three success measures have nothing to
 * feed them today. This catalogue is what makes two of them computable, and
 * makes Seller Collects orders identifiable in the analytics stream so the
 * third can be joined up outside the app.
 *
 * ## The catalogue
 *
 * Every event below is a custom Segment `track` call carrying the common
 * property set in `buildSellerCollectsProperties`, plus the extras named here.
 * All of them are gated on `seller-collects-buyer-experience` and on the
 * listing or order actually being Seller Collects.
 *
 * | Event | Fires when | Extra properties |
 * | --- | --- | --- |
 * | `seller_collects_listing_viewed` | a Seller Collects listing page is opened | `sale_kind` |
 * | `seller_collects_explainer_opened` | the tag's explainer is opened | `surface` |
 * | `seller_collects_education_expanded` | "what happens if you win" is expanded | `sale_kind` |
 * | `seller_collects_bid_confirmed` | a bid or Buy Now purchase is confirmed | `sale_kind` |
 * | `seller_collects_order_viewed` | a Seller Collects order page is opened | `order_id` |
 * | `seller_collects_onboarding_shown` | the first-win modal is shown | `order_id` |
 * | `seller_collects_onboarding_dismissed` | the first-win modal is dismissed | `order_id` |
 * | `seller_collects_fee_payment_started` | the buyer follows the Balance fee link | `order_id`, `surface` |
 *
 * `seller_collects_fee_payment_started` fires from two surfaces: the order
 * page's sticky bar (hidden above the medium breakpoint, so phones only) and
 * the desktop "Payment Due to B-Stock" card's Balance link, via a callback
 * prop on fe-core's `SellerCollectsFeeStateCard`. Read the measure per
 * `surface` if the two need to be told apart.
 *
 * `buyer_premium_applies` is `null` on the grid and List View surfaces by
 * design: the fee posture comes from the seller's subscription, and resolving
 * it per card would cost a request per storefront on the page. It is the same
 * reason the tag's own copy makes no fee claim there.
 *
 * ## What this does not cover
 *
 * The PRD's CX-ticket-rate measure cannot be produced from app events at all:
 * it needs the support system joined to orders. What this catalogue
 * contributes is making a Seller Collects order identifiable in the stream;
 * the join itself belongs to the data team on DAT-2782 and is not done here.
 * Treating the events alone as delivering that measure would overstate them.
 *
 * Two emit sites are deliberately absent, so neither reads as an oversight:
 *
 * - **The bid-sheet upload modal's tag.** It is a bulk-upload flow rather than
 *   a browse surface, its tag carries no sale kind, and a row there is a
 *   pasted lot id rather than a listing the buyer navigated to.
 * - **An explainer opened on the grid or List View before DET-2055.** Both are
 *   wired, but `sellerCollects` is not yet in the search document, so the tag
 *   does not render there and the events cannot fire until that lands. When it
 *   does, each tag on a page resolves the buyer's history for itself — cheap
 *   per card, and deduped by React Query, but worth lifting to a page-level
 *   provider once there is a real card count to measure it against.
 *
 * `seller_collects_bid_confirmed` carries `sale_kind` by what was confirmed —
 * `bid` from the bid modal, `purchase` from Buy Now — not by what the listing
 * offers, so a max bid on a listing that also sells outright is still a bid.
 * It fires when the buyer confirms, **not** when the bid is accepted: a
 * rejected bid that the buyer retries reports twice. Read it as intent to bid
 * rather than as bids placed, and join it to the order for the latter. Moving
 * it onto the mutation's success would mean threading analytics through the
 * bid path, which is not a change an instrumentation ticket should make.
 *
 * `seller_collects_explainer_opened` counts a hover-open on desktop, not only
 * a click: the explainer opens on hover, focus or tap, and the event means
 * what the buyer saw. Filter on the client type if the hover traffic is noise.
 *
 * No event carries anything about the buyer beyond the account and person ids
 * that `useAnalytics` already attaches to every call in the estate.
 */

/** Custom (non-`button_click`) Segment events emitted for Seller Collects. */
export const SellerCollectsEvent = {
  ListingViewed: 'seller_collects_listing_viewed',
  ExplainerOpened: 'seller_collects_explainer_opened',
  EducationExpanded: 'seller_collects_education_expanded',
  BidConfirmed: 'seller_collects_bid_confirmed',
  OrderViewed: 'seller_collects_order_viewed',
  OnboardingShown: 'seller_collects_onboarding_shown',
  OnboardingDismissed: 'seller_collects_onboarding_dismissed',
  FeePaymentStarted: 'seller_collects_fee_payment_started',
} as const

export type SellerCollectsEventName =
  (typeof SellerCollectsEvent)[keyof typeof SellerCollectsEvent]

/**
 * Snake_case property keys, used as computed object keys so a spelling is
 * never repeated as a bare literal.
 */
export const SellerCollectsProperty = {
  SellerCollects: 'seller_collects',
  StorefrontName: 'storefront_name',
  StorefrontId: 'storefront_id',
  BuyerPremiumApplies: 'buyer_premium_applies',
  IsFirstSellerCollects: 'is_first_seller_collects',
  ListingId: 'listing_id',
  OrderId: 'order_id',
  SaleKind: 'sale_kind',
  Surface: 'surface',
} as const

/**
 * Where a buyer opened the explainer, or started the fee payment, from. The
 * tag appears on four surfaces and the fee link on two; without this the
 * events would say a buyer opened something but not where they were.
 */
export const SellerCollectsSurface = {
  ListingDetails: 'listing_details',
  ListingGrid: 'listing_grid',
  ListView: 'list_view',
  OrderStickyBar: 'order_sticky_bar',
  OrderPaymentCard: 'order_payment_card',
} as const

export type SellerCollectsSurfaceName =
  (typeof SellerCollectsSurface)[keyof typeof SellerCollectsSurface]
